/** Admin error email template mapping (T01–T09, T00). */
import { buildClientAdminErrorEmailHtml } from "./admin-error-email-html";
export const SUBMITTING_USER_FIX_TEMPLATE_IDS = new Set([
  "T01-SUBMITTING-USER-SUBSTITUTE",
  "T02-SUBMITTING-USER-NOT-FOUND",
]);

/** Production QuickLinks host for links in admin emails and previews. */
export const QUICKLINKS_PROD_HOST = "https://www.goodleap.dev";

const CRITICAL_ACTION_BULLETS = [
  "Add or reactivate an eligible submitting user",
  "Update the link with an active offer",
  "Replace the deleted or removed channel",
  "Confirm API key status",
  "Coordinate with the partner if account setup needs to be corrected",
];

/**
 * Host for fix-submitting-user and other public QuickLinks URLs.
 * Local dev and prod admin UI should use www.goodleap.dev unless explicitly on sandbox.
 */
export function getQuicklinksPublicHost() {
  if (typeof window !== "undefined" && window.location?.hostname) {
    const { hostname, protocol } = window.location;
    if (hostname === "sandbox.goodleap.dev") {
      return `${protocol}//sandbox.goodleap.dev`;
    }
    if (hostname === "www.goodleap.dev" || hostname.endsWith(".goodleap.dev")) {
      return `${protocol}//${hostname}`;
    }
  }
  return QUICKLINKS_PROD_HOST;
}

/** @deprecated use getQuicklinksPublicHost() */
export const QUICKLINKS_PUBLIC_HOST = QUICKLINKS_PROD_HOST;

export const ADMIN_ERROR_EMAIL_TEMPLATES = [
  {
    id: "T01-SUBMITTING-USER-SUBSTITUTE",
    label: "Submitting user substitute",
    category: "USER REPLACEMENT",
    emailTone: "urgent-fallback",
    subject: "URGENT: GoodLink Error Detected",
    summary: "Submission completed using a backup submitting user.",
    issueDetected: "Submitting user unavailable",
    patterns: [/using substitute/i, /was not found;\s*using substitute/i],
  },
  {
    id: "T02-SUBMITTING-USER-NOT-FOUND",
    label: "Submitting user not found",
    category: "SUBMITTING USER",
    emailTone: "critical",
    subject: "CRITICAL – BD Action Required to Restore GoodLink Functionality",
    summary: "No eligible submitting user is available for this GoodLink.",
    issueDetected: "No eligible submitting user available",
    actionBullets: [
      "Add or reactivate an eligible submitting user",
      "Coordinate with the partner if account setup needs to be corrected",
    ],
    patterns: [
      /no submitting user found and no eligible substitute/i,
      /no eligible substitute user found/i,
    ],
  },
  {
    id: "T03-CONNECTED-ORG-TOKEN",
    label: "Connected org token",
    category: "Share app creation",
    emailTone: "critical",
    subject: "CRITICAL – BD Action Required to Restore GoodLink Functionality",
    summary: "The partner account connection could not be established.",
    issueDetected: "Account connection could not be established",
    actionBullets: [
      "Confirm API key status",
      "Coordinate with the partner if account setup needs to be corrected",
    ],
    patterns: [/connected org connection could not be made/i, /elevated access token/i],
  },
  {
    id: "T04-OFFERS-CATALOG-EMPTY",
    label: "Offers catalog empty",
    category: "OFFERS",
    emailTone: "critical",
    subject: "CRITICAL – BD Action Required to Restore GoodLink Functionality",
    summary: "Loan offers are unavailable or do not match this GoodLink.",
    issueDetected: "Loan offers unavailable or channel configuration issue",
    actionBullets: [
      "Update the link with an active offer",
      "Replace the deleted or removed channel",
      "Coordinate with the partner if account setup needs to be corrected",
    ],
    patterns: [
      /no offers for this channel/i,
      /configured offer ids did not match/i,
      /no configured offers to fall back/i,
      /error getting offers, no offers returned/i,
    ],
  },
  {
    id: "T05-OFFERS-FETCH-FAILED",
    label: "Offers fetch failed",
    category: "OFFERS",
    emailTone: "critical",
    subject: "CRITICAL – BD Action Required to Restore GoodLink Functionality",
    summary: "Loan offers could not be loaded for this GoodLink.",
    issueDetected: "Loan offers could not be loaded",
    actionBullets: [
      "Verify partner API keys in SSM (401/403 = bad or missing keys)",
      "Update the link with an active offer",
      "Replace the deleted or removed channel",
      "Coordinate with the partner if account setup needs to be corrected",
    ],
    patterns: [
      /error getting offers, offer fetch failed/i,
      /error getting offers: API authentication failed/i,
    ],
  },
  {
    id: "T06-PAYMENTS-CATEGORIES-LIMITATIONS",
    label: "Payments / categories / limitations",
    category: "OFFERS",
    emailTone: "critical",
    subject: "CRITICAL – BD Action Required to Restore GoodLink Functionality",
    summary: "Product pricing or category setup is incomplete for this GoodLink.",
    issueDetected: "Product pricing or category configuration issue",
    actionBullets: [
      "Update the link with an active offer",
      "Replace the deleted or removed channel",
      "Coordinate with the partner if account setup needs to be corrected",
    ],
    patterns: [
      /error getting payments/i,
      /error getting categories/i,
      /error getting limitations/i,
    ],
  },
  {
    id: "T07-OFFERS-API-ERROR",
    label: "Offers API error",
    category: "OFFERS",
    emailTone: "critical",
    subject: "CRITICAL – BD Action Required to Restore GoodLink Functionality",
    summary: "Loan offers could not be loaded for this GoodLink.",
    issueDetected: "Loan offers could not be loaded",
    actionBullets: [
      "Update the link with an active offer",
      "Replace the deleted or removed channel",
      "Coordinate with the partner if account setup needs to be corrected",
    ],
    patterns: [/^error getting offers\b/i],
  },
  {
    id: "T08-SUNRUN-CREATE-FAILED",
    label: "SunRun app creation",
    category: "Share app creation",
    emailTone: "critical",
    subject: "CRITICAL – BD Action Required to Restore GoodLink Functionality",
    summary: "This SunRun GoodLink could not be created.",
    issueDetected: "SunRun GoodLink could not be created",
    actionBullets: CRITICAL_ACTION_BULLETS,
    patterns: [/error creating sunrun shared app/i],
  },
  {
    id: "T09-LOAN-SHARE-CREATE-FAILED",
    label: "Loan share / create app",
    category: "Share app creation",
    emailTone: "critical",
    subject: "CRITICAL – BD Action Required to Restore GoodLink Functionality",
    summary: "This GoodLink could not be created.",
    issueDetected: "GoodLink could not be created",
    actionBullets: CRITICAL_ACTION_BULLETS,
    patterns: [/error creating shared app/i, /error creating.*shared app/i],
  },
  {
    id: "T00-OTHER",
    label: "Other / unclassified",
    category: "OTHER",
    emailTone: "critical",
    subject: "CRITICAL – BD Action Required to Restore GoodLink Functionality",
    summary: "An unexpected issue occurred with this GoodLink.",
    issueDetected: "An unexpected error occurred with this GoodLink",
    actionBullets: CRITICAL_ACTION_BULLETS,
    patterns: [],
  },
];

const DEFAULT_TEMPLATE = ADMIN_ERROR_EMAIL_TEMPLATES.find(
  (template) => template.id === "T00-OTHER"
);

export function templateIncludesFixLink(templateId) {
  return SUBMITTING_USER_FIX_TEMPLATE_IDS.has(String(templateId ?? "").trim());
}

export function logShouldIncludeFixSubmittingUserLink(log) {
  const template = resolveAdminErrorEmailTemplate(
    log?.error,
    log?.emailTemplateId
  );
  return templateIncludesFixLink(template?.id);
}

/** Relative path for the public fix submitting user page. */
export function fixSubmittingUserPath(requestId, sharedAppId) {
  const id = String(requestId ?? "").trim();
  if (!id) return "";
  const base = `/fix-submitting-user/${encodeURIComponent(id)}`;
  const sharedId = String(sharedAppId ?? "").trim();
  if (!sharedId) return base;
  return `${base}?sharedAppId=${encodeURIComponent(sharedId)}`;
}

export function fixSubmittingUserAbsoluteUrl(
  requestId,
  sharedAppId,
  host = getQuicklinksPublicHost()
) {
  const path = fixSubmittingUserPath(requestId, sharedAppId);
  if (!path) return "";
  return `${String(host).replace(/\/$/, "")}${path}`;
}

/** Resolve email template metadata from stored id or error message text. */
export function resolveAdminErrorEmailTemplate(errorMessage, emailTemplateId) {
  const storedId = String(emailTemplateId ?? "").trim();
  if (storedId) {
    const byId = ADMIN_ERROR_EMAIL_TEMPLATES.find(
      (template) => template.id === storedId
    );
    if (byId) return byId;
  }

  const msg = String(errorMessage ?? "").trim();
  if (!msg) return DEFAULT_TEMPLATE;

  for (const template of ADMIN_ERROR_EMAIL_TEMPLATES) {
    if (template.id === "T00-OTHER") continue;
    if (template.patterns.some((pattern) => pattern.test(msg))) {
      return template;
    }
  }

  return DEFAULT_TEMPLATE;
}

export function emailTemplateTableLabel(emailTemplateId, errorMessage) {
  const template = resolveAdminErrorEmailTemplate(errorMessage, emailTemplateId);
  return template?.id?.replace(/^T\d+-/, "") ?? "—";
}

export function formatEmailSentStatus(notifyEmail, { sendFailed = false } = {}) {
  if (sendFailed) {
    return { label: "Failed", tone: "failed" };
  }
  if (notifyEmail === true) {
    return { label: "Sent", tone: "sent" };
  }
  if (notifyEmail === false) {
    return { label: "Not Sent", tone: "not-sent" };
  }
  return { label: "Not Sent", tone: "not-sent" };
}

/** @deprecated use formatEmailSentStatus */
export function formatNotifyEmailSent(notifyEmail) {
  return formatEmailSentStatus(notifyEmail).label;
}

export const ADMIN_ERROR_FROM_EMAIL = "apisupport@goodleap.com";
export const ADMIN_ERROR_DEFAULT_ADMIN_EMAIL = "sgarcia@goodleap.com";

function normalizeEmail(value) {
  const email = String(value ?? "").trim();
  if (!email || !email.includes("@")) return "";
  return email;
}

function formatEmailDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "America/Los_Angeles",
    });
  } catch {
    return String(value);
  }
}

function parseConfiguredSubmittingUser(errorMessage) {
  const match = String(errorMessage ?? "").match(
    /Configured submitting user "([^"]+)"/i
  );
  return match ? match[1].trim() : "";
}

function parseSubstituteFallback(errorMessage) {
  const msg = String(errorMessage ?? "");
  const quoted = msg.match(/using substitute(?: user)?\s+"([^"]+)"/i);
  if (quoted) return quoted[1].trim();
  const email = msg.match(
    /using substitute(?: user)?\s+([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/i
  );
  if (email) return email[1].trim();
  // Trailing ")." from role suffix: "... substitute foo. (Role)"
  const plain = msg.match(
    /using substitute(?: user)?\s+(.+?)(?:\s*\(|\.\s*$)/i
  );
  if (plain) return plain[1].trim().replace(/\.$/, "");
  return "A backup submitting user was used";
}

/** Client-facing email body (GoodLink Error Messaging Templates.docx). */
export function buildClientAdminErrorEmailBody({
  template,
  accountName,
  formUrl,
  detectedAt,
  errorMessage,
  fixUrl = "",
}) {
  const issueLine = (() => {
    const base = template?.issueDetected || "An issue was detected with this GoodLink";
    const configured = parseConfiguredSubmittingUser(errorMessage);
    if (template?.emailTone === "urgent-fallback" && configured) {
      return `${base} (${configured})`;
    }
    return base;
  })();

  if (template?.emailTone === "urgent-fallback") {
    const lines = [
      "Hello,",
      "",
      "A GoodLink submission was completed successfully, but only because fallback logic was used.",
      "",
      "This means the original configuration tied to the link is no longer fully valid. While the borrower was able to continue, we want to confirm that the link is still up to date with the contractor's desired preferences.",
      "",
      `Issue detected: ${issueLine}`,
      `Fallback used: ${parseSubstituteFallback(errorMessage)}`,
      `Partner / account: ${accountName || "—"}`,
      `GoodLink: ${formUrl || "—"}`,
      `Date/time detected: ${formatEmailDate(detectedAt)}`,
      "",
      "Action required:",
      "Please review the link configuration as soon as possible and confirm if the correct submitting user is in use. Update the link if needed to prevent future submission failures. To help facilitate a resolution, the primary contact for the customer has been cc'd on this thread.",
    ];
    if (fixUrl) {
      lines.push("", `Fix submitting user: ${fixUrl}`);
    }
    lines.push("", "Thank you,", "Customer Success and Innovation Team");
    return lines.join("\n");
  }

  const bullets = template?.actionBullets?.length
    ? template.actionBullets
    : CRITICAL_ACTION_BULLETS;

  const lines = [
    "Hello,",
    "",
    "A GoodLink submission failed, and the system was unable to resolve the issue automatically. The link is not currently working as expected and requires BD review before future submissions can proceed successfully.",
    "",
    `Issue detected: ${issueLine}`,
    `Partner / account: ${accountName || "—"}`,
    `GoodLink: ${formUrl || "—"}`,
    `Date/time detected: ${formatEmailDate(detectedAt)}`,
    "",
    "Action required:",
    "Please review the partner's setup and update the impacted GoodLink. Depending on the issue, this may require one or more of the following:",
    ...bullets.map((item) => `\t• ${item}`),
    "",
    "Once the issue is resolved, please confirm that the link has been reviewed and is ready for use. To help facilitate a resolution, the primary contact for the customer has been cc'd on this thread.",
  ];
  if (fixUrl) {
    lines.push("", `Fix submitting user: ${fixUrl}`);
  }
  lines.push("", "Thank you,", "Customer Success and Innovation Team");
  return lines.join("\n");
}

/** Mirror Lambda recipient routing using denormalized AdminErrorLog fields. */
export function buildAdminErrorEmailDestinationsFromLog(log) {
  const admin =
    normalizeEmail(ADMIN_ERROR_DEFAULT_ADMIN_EMAIL) ||
    ADMIN_ERROR_DEFAULT_ADMIN_EMAIL;
  const partner = normalizeEmail(log?.partnerPrimaryContactEmail);
  const accountOwner = normalizeEmail(log?.salesforceAccountOwnerEmail);

  if (!partner) {
    return {
      ToAddresses: [admin],
      CcAddresses: [],
      mode: "admin-fallback",
    };
  }

  const cc = [admin, accountOwner]
    .map(normalizeEmail)
    .filter(Boolean)
    .filter((email) => email.toLowerCase() !== partner.toLowerCase())
    .filter((email, index, list) => list.indexOf(email) === index);

  return {
    ToAddresses: [partner],
    CcAddresses: cc,
    mode: "partner",
  };
}

/** Rebuild subject + body to match the SES alert sent from Lambda. */
export function buildAdminErrorEmailPreview(log) {
  const template = resolveAdminErrorEmailTemplate(
    log?.error,
    log?.emailTemplateId
  );
  const templateId = template?.id ?? "T00-OTHER";
  const sharedAppId = String(log?.sharedAppId ?? "").trim();
  const fixSubmittingUserUrl =
    templateIncludesFixLink(templateId) && log?.requestId
      ? fixSubmittingUserAbsoluteUrl(log.requestId, sharedAppId)
      : "";

  const destinations = buildAdminErrorEmailDestinationsFromLog(log);

  const emailContext = {
    template,
    accountName: log?.accountName,
    formUrl: log?.formUrl,
    detectedAt: log?.createdAt,
    errorMessage: log?.error,
    fixUrl: fixSubmittingUserUrl,
  };

  return {
    from: ADMIN_ERROR_FROM_EMAIL,
    to: destinations.ToAddresses,
    cc: destinations.CcAddresses,
    subject: template?.subject || "GoodLink Error Detected",
    body: buildClientAdminErrorEmailBody(emailContext),
    htmlBody: buildClientAdminErrorEmailHtml({
      ...emailContext,
      formatEmailDate,
      parseConfiguredSubmittingUser,
      parseSubstituteFallback,
      criticalActionBullets: CRITICAL_ACTION_BULLETS,
    }),
    fixSubmittingUserUrl,
    templateId,
    recipientMode: destinations.mode,
  };
}
